import { MutableRefObject, useRef } from 'react';

import Button from '@components/button/Button';
import Icon, { IconSize } from '@components/icon/Icon';
import useOnClickOutside from '@utils/hooks';

interface ConfirmationModalV2Props {
  title?: string;
  description?: string;
  className?: string;
  iconName?: string;
  iconSize?: IconSize;
  confirmButtonLabel?: string;
  onConfirm: () => void;
  cancelButtonLabel?: string;
  onCancel: () => void;
  isClickOutsideRequired?: boolean;
}

const ConfirmationModalV2 = ({
  title,
  description,
  className = '',
  iconName,
  iconSize = '48',
  onConfirm,
  onCancel,
  confirmButtonLabel = 'Confirm',
  cancelButtonLabel = 'Cancel',
  isClickOutsideRequired = true
}: ConfirmationModalV2Props) => {
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  useOnClickOutside(modalRef, (e) => {
    if (isClickOutsideRequired && onCancel) {
      e.stopPropagation();
      onCancel();
    }
  });

  return (
    <div className="fixed left-0 top-0 z-[60] flex size-full items-center justify-center">
      {/* Modal Overlay */}
      <div className="absolute size-full bg-black opacity-30" />

      {/* Modal Main Content */}
      <div
        className={`relative mx-auto flex max-h-[98%] w-120 flex-col overflow-hidden rounded-lg bg-cofeeNeutral1 ${className}`}
        ref={modalRef}
      >
        <div className="flex flex-col items-center justify-center gap-8 p-6">
          {iconName && (
            <div className="flex items-center justify-center">
              <Icon name={iconName} size={iconSize} />
            </div>
          )}
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            {title && (
              <span className="text-coffeeNeutral10 text-lg font-semibold">
                {title}
              </span>
            )}
            {description && (
              <span className="text-coffeeNeutral8 text-base font-normal">
                {description}
              </span>
            )}
          </div>
          <div className="flex w-full items-center justify-center gap-2">
            <Button
              variant="tertiary"
              handleClick={onCancel}
              className="w-full"
            >
              {cancelButtonLabel}
            </Button>
            <Button
              variant="primary"
              handleClick={onConfirm}
              className="w-full !text-cofeeNeutral1"
            >
              {confirmButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModalV2;
